import { create } from 'zustand';
import { onlineOrdersAPI } from '@matix/api-client';
import type { OnlineOrderAlertRow } from '@matix/types';
import { playChime } from '../features/delivery-manager/useDeliveryBoard';

/**
 * The cashier's "new web order" alert (WooCommerce, Ordable, … — anything that
 * arrives through an online ordering channel).
 *
 * The server lists the branch's web orders of the last 24 hours; this store
 * decides which ones THIS till has not seen yet. "Seen" is per till (kept in
 * the browser) on purpose: every counter is alerted until someone standing at
 * it has looked. An alert is keyed by order AND status, so an order the shop
 * later cancels alerts again as a cancellation.
 *
 * The very first time a till runs this, everything already in the window is
 * marked seen silently — deploying the feature must not ring 24 hours of old
 * orders at once.
 *
 * AUTO-PRINT is per till too, and off until someone switches it on at that
 * till: a branch with three tills wants ONE copy of each web order, not three.
 * When on, every new imported order goes onto `printQueue` exactly once
 * (`printed` is remembered in the browser, so a reload never prints again) and
 * `OnlineOrdersAutoPrinter` prints it through the till's normal reprint path —
 * straight to the default printer on a till running Chrome with
 * --kiosk-printing, otherwise through the print dialog. Switching it on marks
 * what is already listed as printed: it never prints the backlog.
 *
 * An order this till PRINTS needs no pop-up (khwada, 2026-10-20: "as long as
 * the auto-printer prints, we don't need the popup"): while it waits in
 * `printQueue` it is held back from the alert, and a successful print marks
 * it seen (`acknowledgeReceipt`). A failed print leaves it unseen, so the
 * pop-up appears as before; a shop cancellation is never printed and still
 * alerts. Tills without auto-print are unchanged.
 *
 * A company can make auto-print the DEFAULT (POS_WebOrdersAutoPrint, sent as
 * `auto_print_default`): a till that never touched its own switch then prints
 * — again from "now", never the backlog. Touching the switch makes the till's
 * own choice win from then on.
 */

const MAX_SEEN = 400;

/** The refresh in flight, if any: overlapping calls share it (they used to queue the same order twice). */
let inFlight: Promise<void> | null = null;
const keyOf = (r: OnlineOrderAlertRow): string => `${r.id}:${r.status}`;

function loadSet(storageKey: string): Set<string> | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw == null) return null;
    const list = JSON.parse(raw);
    return Array.isArray(list) ? new Set(list.map(String)) : new Set();
  } catch {
    return new Set();
  }
}

function saveSet(storageKey: string, set: Set<string>): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify([...set].slice(-MAX_SEEN)));
  } catch {
    /* private window / storage blocked — the alert just repeats after a reload */
  }
}

/** '1' / '0' as stored, or null when this till never chose. */
function loadFlag(storageKey: string): boolean | null {
  try {
    const v = localStorage.getItem(storageKey);
    return v == null ? null : v === '1';
  } catch {
    return null;
  }
}

function saveFlag(storageKey: string, on: boolean): void {
  try {
    localStorage.setItem(storageKey, on ? '1' : '0');
  } catch {
    /* storage blocked — the switch lasts until the page reloads */
  }
}

interface OnlineOrdersState {
  storageKey: string | null;
  rows: OnlineOrderAlertRow[];
  seen: Set<string>;
  panelOpen: boolean;
  /** This till prints new web orders by itself. */
  autoPrint: boolean;
  /** The till's own switch was set (else the company default applies). */
  autoPrintChosen: boolean;
  /** Web order ids this till has already printed (or chose not to). */
  printed: Set<string>;
  /** Receipts waiting to be printed by OnlineOrdersAutoPrinter, oldest first. */
  printQueue: number[];
  /** Point the store at this till (tenant + branch); loads what it has seen. */
  attach: (storageKey: string) => void;
  refresh: () => Promise<void>;
  acknowledge: (row: OnlineOrderAlertRow) => void;
  acknowledgeAll: () => void;
  setPanelOpen: (open: boolean) => void;
  setAutoPrint: (on: boolean) => void;
  /** The printer took the head of the queue (or gave up on it). */
  shiftPrintQueue: () => void;
  /** The auto-printer printed this receipt: its order needs no pop-up. */
  acknowledgeReceipt: (receiptId: number) => void;
}

export const useOnlineOrders = create<OnlineOrdersState>((set, get) => ({
  storageKey: null,
  rows: [],
  seen: new Set(),
  panelOpen: false,
  autoPrint: false,
  autoPrintChosen: false,
  printed: new Set(),
  printQueue: [],

  attach: (storageKey) => {
    if (get().storageKey === storageKey) return;
    set({
      storageKey,
      rows: [],
      seen: loadSet(storageKey) ?? new Set(['__baseline__']),
      autoPrint: loadFlag(`${storageKey}:autoprint`) ?? false,
      autoPrintChosen: loadFlag(`${storageKey}:autoprint`) != null,
      printed: loadSet(`${storageKey}:printed`) ?? new Set(),
      printQueue: [],
    });
  },

  refresh: () => {
    // One refresh at a time: the timer, the focus/visibility events and attach
    // used to overlap, and two overlapping refreshes queued the same order twice.
    if (inFlight) return inFlight;
    inFlight = (async () => {
    const { storageKey } = get();
    if (!storageKey) return;
    let rows: OnlineOrderAlertRow[];
    let companyDefault = false;
    try {
      const res = await onlineOrdersAPI.recent();
      rows = res.rows;
      companyDefault = res.auto_print_default === true;
    } catch {
      return; // offline / server busy — the next poll tries again
    }
    // No choice made at this till: follow the company. Switching on this way
    // starts from now — what is listed already counts as printed.
    if (!get().autoPrintChosen && companyDefault !== get().autoPrint) {
      if (companyDefault) {
        const printedNow = new Set([...get().printed, ...rows.map((r) => String(r.id))]);
        saveSet(`${storageKey}:printed`, printedNow);
        set({ autoPrint: true, printed: printedNow });
      } else {
        set({ autoPrint: false, printQueue: [] });
      }
    }
    const before = get().seen;
    let seen = before;
    let printed = get().printed;
    let printQueue = get().printQueue;
    if (before.has('__baseline__')) {
      // First run on this till: everything already here counts as seen — and
      // as printed, so switching auto-print on later never prints history.
      seen = new Set(rows.map(keyOf));
      saveSet(storageKey, seen);
      printed = new Set([...printed, ...rows.map((r) => String(r.id))]);
      saveSet(`${storageKey}:printed`, printed);
    } else {
      const previouslyUnseen = new Set(get().rows.filter((r) => !before.has(keyOf(r))).map(keyOf));
      const fresh = rows.some((r) => !before.has(keyOf(r)) && !previouslyUnseen.has(keyOf(r)));
      if (fresh) playChime();
      if (get().autoPrint) {
        // Oldest first; only real (imported) orders; each id once, ever.
        const toPrint = [...rows]
          .reverse()
          .filter((r) => r.status === 'imported' && !printed.has(String(r.id)) && !printQueue.includes(r.receipt_id));
        if (toPrint.length > 0) {
          printed = new Set([...printed, ...toPrint.map((r) => String(r.id))]);
          saveSet(`${storageKey}:printed`, printed);
          printQueue = [...printQueue, ...toPrint.map((r) => r.receipt_id)];
        }
      }
    }
    set({ rows, seen, printed, printQueue });
    })().finally(() => { inFlight = null; });
    return inFlight;
  },

  acknowledge: (row) => {
    const { storageKey, seen } = get();
    const next = new Set(seen);
    next.add(keyOf(row));
    if (storageKey) saveSet(storageKey, next);
    set({ seen: next });
  },

  acknowledgeAll: () => {
    const { storageKey, seen, rows } = get();
    const next = new Set(seen);
    for (const r of rows) next.add(keyOf(r));
    if (storageKey) saveSet(storageKey, next);
    set({ seen: next });
  },

  setPanelOpen: (panelOpen) => set({ panelOpen }),

  setAutoPrint: (on) => {
    const { storageKey, rows, printed } = get();
    if (!storageKey) return;
    saveFlag(`${storageKey}:autoprint`, on);
    set({ autoPrintChosen: true });
    if (on) {
      // Never the backlog: what is listed now counts as already printed.
      const next = new Set([...printed, ...rows.map((r) => String(r.id))]);
      saveSet(`${storageKey}:printed`, next);
      set({ autoPrint: true, printed: next });
    } else {
      set({ autoPrint: false, printQueue: [] });
    }
  },

  shiftPrintQueue: () => set({ printQueue: get().printQueue.slice(1) }),

  acknowledgeReceipt: (receiptId) => {
    const { storageKey, seen, rows } = get();
    const next = new Set(seen);
    for (const r of rows) if (r.receipt_id === receiptId && r.status === 'imported') next.add(keyOf(r));
    if (storageKey) saveSet(storageKey, next);
    set({ seen: next });
  },
}));

/**
 * Web orders this till has not acknowledged yet, oldest first. One waiting for
 * the auto-printer is held back: it pops up only if that print fails.
 */
export function unseenOnlineOrders(s: Pick<OnlineOrdersState, 'rows' | 'seen'> & { printQueue?: number[] }): OnlineOrderAlertRow[] {
  const printing = new Set(s.printQueue ?? []);
  return s.rows.filter((r) => !s.seen.has(keyOf(r)) && !(r.status === 'imported' && printing.has(r.receipt_id))).reverse();
}
